import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUpsertAppointment } from '../../../features/appointment-upsert';
import { useUpsertTask } from '../../../features/task-upsert';
import { routes } from '../../../shared/config/routes';
import { resolveBlockTarget } from '../model/blockTarget';
import { LANE_HEIGHT, ROW_PADDING_Y, spanWidthPx, toXPx } from '../../../shared/lib/schedule';
import type { DragHandle } from '../model/buildSchedulePatch';
import type { DragPolicy, Handle } from '../model/dragPolicy';
import { ACTIVE_SEGMENT_KIND } from '../../../shared/lib/schedule';
import { buildReschedulingLine } from '../model/useDispositionBoard';
import {
  RESCHEDULING_GHOST_HATCH,
  RESCHEDULING_GHOST_OUTLINE,
  showsReschedulingState,
} from '../../../shared/lib/schedule';
import { useBlockHoverCard } from '../model/useBlockHoverCard';
import type { BoardBlock } from '../model/boardTypes';
import { BlockInfoCard } from './BlockInfoCard';
import { HandleStrip } from './HandleStrip';
import { SegmentCell } from './SegmentCell';

/**
 * Shared by the inert and the clickable variant, so they cannot drift apart.
 *
 * `isolate` is load-bearing: the handle strips, the attachment badge and the Umdisponieren pill all
 * carry `z-10`, and without a stacking context here that 10 competes with the sticky technician
 * column instead of with their own siblings — they would paint on top of it as soon as the board is
 * scrolled right. See `BOARD_LAYER`.
 */
const BLOCK_CLASS = 'absolute isolate flex gap-px overflow-hidden rounded';

interface Props {
  block: BoardBlock;
  policy: DragPolicy;
  /** This block is the one currently being dragged — dim it, the ghost shows where it will land. */
  isDragging: boolean;
  onDragStart: (block: BoardBlock, handle: DragHandle, e: React.PointerEvent) => void;
  /** True when the gesture that just ended was a drag, so the trailing click is ignored. */
  wasDragged: () => boolean;
  /**
   * Display-only. The block still renders in full — reading the day is why you are here — but it
   * neither opens its modal nor drags: the click belongs to the slot being placed, and a block that
   * opened a different form mid-placement would be a trap.
   *
   * True in planning mode, and while an upsert form is open on the board: the form is the one thing
   * being edited, and a second block volunteering its own form beside it is that same trap.
   */
  inert?: boolean;
  /**
   * A drag is in progress somewhere on the board. Suppresses the hover card outright: the pointer is
   * busy moving a block, and a card opening under it would sit between the dispatcher and the ghost
   * showing where the block will land.
   *
   * Board-wide rather than per-block, because the drag crosses rows — the card must stay away over
   * the blocks being dragged *past*, not just the one being dragged.
   */
  dragActive?: boolean;
  /**
   * Width of the docked upsert panel, or 0. Handed to the hover card so it flips to the left of the
   * block rather than opening underneath a panel that outranks it. See `BOARD_LAYER.hoverCard`.
   */
  reservedRightPx?: number;
}

/**
 * One entry on the board — appointments, tasks, absences and breaks all render through here.
 *
 * The deleted timeline had four near-identical block components; this is deliberately one. The
 * content layout is that timeline's, though: `HH:mm` on top, subject under it, Gewerk and
 * Auftragsnummer pinned to the bottom.
 */
export const ScheduleBlock = ({
  block,
  policy,
  isDragging,
  onDragStart,
  wasDragged,
  inert = false,
  dragActive = false,
  reservedRightPx = 0,
}: Props) => {
  const { span, segments } = block;

  const { openEdit: openAppointment } = useUpsertAppointment();
  const { openEdit: openTask } = useUpsertTask();
  const navigate = useNavigate();

  // Which resize strip the pointer is over. Ephemeral and local to one block — `useState` is where
  // `/react-spa-fsd`'s state table puts it.
  const [hoveredHandle, setHoveredHandle] = useState<Handle | null>(null);

  /**
   * Only Termin and Aufgabe get a card. An Abwesenheit says all it has to say in three lines and a
   * Pause has no owner row to describe, so both keep the native `title` below — a card that opened
   * to repeat the two words already printed on the block would be worse than nothing.
   */
  const hasCard = block.owner?.kind === 'appointment' || block.owner?.kind === 'task';

  const hover = useBlockHoverCard();
  const { open: openCard, scheduleClose, cancelClose, closeNow } = hover;

  // A drag that starts while a card is up must take it down — and `dragActive` also latches the card
  // shut for the rest of the gesture, since the effect re-runs the moment the flag clears.
  useEffect(() => {
    if (dragActive) closeNow();
  }, [dragActive, closeNow]);

  const target = resolveBlockTarget(block.entry);

  const blockWidth = spanWidthPx(span);
  const totalMinutes = segments.reduce((sum, s) => sum + s.minutes, 0);

  const handleClick = (e: React.MouseEvent) => {
    if (!target) return;
    // A drag ends with a click too — opening the modal here would fight the drop.
    if (wasDragged()) return;
    e.stopPropagation();

    switch (target.kind) {
      case 'appointment':
        openAppointment(target.id);
        break;
      case 'task':
        openTask(target.id);
        break;
      case 'absence':
        // Absences have no upsert modal yet — the Zeitmanagement page owns that form.
        navigate(routes.zeitmanagement.edit(target.id));
        break;
    }
  };

  // Not `block.needsRescheduling`: that field is collision data and stays raw — a flagged Pause
  // still does not reserve its time. This is the display rule, and it excludes a Pause entirely.
  const ghosted = showsReschedulingState(block.entry);

  const style: React.CSSProperties = {
    left: toXPx(span.startMin),
    width: blockWidth,
    top: ROW_PADDING_Y + block.lane * LANE_HEIGHT,
    height: LANE_HEIGHT - 6,
    // Inline, and drawn inwards so it cannot be clipped by the block's own `overflow-hidden`.
    ...(ghosted ? { outline: RESCHEDULING_GHOST_OUTLINE, outlineOffset: '-2px' } : {}),
    // A clipped edge stays square, so it reads as "continues beyond the window".
    borderTopLeftRadius: span.clippedStart ? 0 : undefined,
    borderBottomLeftRadius: span.clippedStart ? 0 : undefined,
    borderTopRightRadius: span.clippedEnd ? 0 : undefined,
    borderBottomRightRadius: span.clippedEnd ? 0 : undefined,
  };

  // The strip the hovered handle would resize. `null` when nothing is hovered, so nothing dims.
  const hoveredKind = hoveredHandle ? ACTIVE_SEGMENT_KIND[hoveredHandle] : null;

  const cells = segments.map((segment) => (
    <SegmentCell
      key={segment.kind}
      segment={segment}
      widthPx={totalMinutes > 0 ? (segment.minutes / totalMinutes) * blockWidth : 0}
      dimmed={hoveredKind != null && hoveredKind !== segment.kind}
    />
  ));

  // A Pause, or an entry whose owner id is missing, has nothing to open — render it inert rather
  // than as a control that does nothing. It may still be draggable, which breaks are.
  // While inert, nothing is interactive: the row's click belongs to the slot being placed, and a
  // block swallowing it would either open the wrong form or eat the pick entirely.
  const interactive = target != null && !inert;
  const draggable = policy.canMove && !inert;

  const px = (minutes: number) => (totalMinutes > 0 ? (minutes / totalMinutes) * blockWidth : 0);
  const travel = segments.find((s) => s.kind === 'travel')?.minutes ?? 0;
  const work = segments.find((s) => s.kind === 'work')?.minutes ?? 0;

  /** Each handle sits on the boundary it moves. */
  const handleOffset: Record<Handle, number> = {
    travel: 0,
    start: 0,
    work: px(travel + work),
    departure: blockWidth,
    end: blockWidth,
  };

  const handleLabel: Record<Handle, string> = {
    travel: 'Anfahrt anpassen',
    work: 'Arbeitszeit anpassen',
    departure: 'Abfahrt anpassen',
    start: 'Beginn anpassen',
    end: 'Ende anpassen',
  };

  return (
    <>
      <div
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={interactive ? handleClick : undefined}
        onKeyDown={
          interactive
            ? (e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                handleClick(e as unknown as React.MouseEvent);
              }
            : undefined
        }
        onPointerDown={
          draggable || hasCard
            ? (e) => {
                // Before the drag, always: whatever this gesture turns out to be — a drag, or a click
                // that opens the form — the card has been overtaken by it.
                closeNow();
                if (draggable) onDragStart(block, 'move', e);
              }
            : undefined
        }
        // The `HandleStrip`s are children, so the pointer crossing one does not leave the block.
        onPointerEnter={hasCard && !dragActive ? (e) => openCard(e.currentTarget) : undefined}
        onPointerLeave={hasCard ? scheduleClose : undefined}
        // Keyboard parity: the block is already a focusable button, so tabbing to it should surface
        // the same detail hovering does.
        onFocus={hasCard ? (e) => openCard(e.currentTarget) : undefined}
        onBlur={hasCard ? scheduleClose : undefined}
        // Only where no card replaced it. Leaving both on would mean the browser's own tooltip fading
        // in on top of the card, saying the same things worse.
        title={hasCard ? undefined : block.tooltip}
        // The tooltip is multi-line; the label is the one line that identifies the block. The flag is
        // spelled out rather than left to the pill — `aria-label` replaces the children entirely, so
        // anything not named here is silent.
        aria-label={
          interactive
            ? [
                ghosted ? buildReschedulingLine(block.entry) : null,
                ...block.tooltip.split('\n').slice(0, 2),
              ]
                .filter(Boolean)
                .join(' ')
            : undefined
        }
        className={[
          BLOCK_CLASS,
          // A white inset ring is the one affordance that reads on every block colour.
          interactive
            ? 'text-left hover:ring-2 hover:ring-inset hover:ring-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white'
            : '',
          draggable ? 'cursor-grab active:cursor-grabbing' : interactive ? 'cursor-pointer' : '',
          // The ghost shows where it is going; a solid copy left behind would read as two blocks.
          isDragging ? 'opacity-30' : '',
        ].join(' ')}
        style={style}
      >
        {cells}

        {/* Over the strips, so travel and departure are hatched too — the whole block is unplaced. */}
        {ghosted && (
          <div
            className="pointer-events-none absolute inset-0"
            style={{ backgroundImage: RESCHEDULING_GHOST_HATCH }}
            aria-hidden
          />
        )}

        {!isDragging &&
          !inert &&
          policy.handles.map((handle) => (
            <HandleStrip
              key={handle}
              leftPx={handleOffset[handle]}
              label={handleLabel[handle]}
              onPointerDown={(e) => {
                closeNow();
                onDragStart(block, handle, e);
              }}
              onHoverChange={(hovered) =>
                setHoveredHandle((current) =>
                  hovered ? handle : current === handle ? null : current,
                )
              }
            />
          ))}
      </div>

      {/*
      A sibling of the block, and portaled out of the board from there. Rendered even while `inert`:
      blocks stay fully drawn in planning mode precisely so the day can be read, and a read-only card
      cannot steal the click that places a slot.
    */}
      {hasCard && hover.anchorRect && !dragActive && (
        <BlockInfoCard
          block={block}
          anchorRect={hover.anchorRect}
          reservedRightPx={reservedRightPx}
          onPointerEnter={cancelClose}
          onPointerLeave={scheduleClose}
          onClose={closeNow}
        />
      )}
    </>
  );
};
